import React, { createContext, useContext, useEffect, useState } from "react";
import { Plan } from "../../Entities/Plan";
import organizationService from "../../Service/organization.service";
import { useAuth } from "../AuthContext/AuthContext";
import commonService from "../../Service/common.service";
import planService from "../../Service/plan.service";

interface AppContextType {
  plans: Plan[];
  addons: Plan[];
  currentRegion: "IND" | "OTHER";
  freePlan: Plan | null;
  creditPlan: Plan | null;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider = ({ children }: { children: React.ReactNode }) => {
  const { isUserLoggedIn } = useAuth();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [addons, setAddons] = useState<Plan[]>([]);
  const [freePlan, setFreePlan] = useState<Plan | null>(null);
  const [currentRegion, setCurrentRegion] = useState<"IND" | "OTHER">("IND");
  const [creditPlan, setCreditPlan] = useState<Plan | null>(null);

  const initializeApp = async () => {
    const allPlansPromise = planService.getAllPlans();
    const regionPromise = commonService.getCurrentRegion();

    const [allPlans, region] = await Promise.all([
      allPlansPromise,
      regionPromise,
    ]);

    const basePlans = allPlans.filter((p) => p.type === "base");
    const addonPlans = allPlans.filter((p) => p.type === "addon");
    const freePlan = allPlans.find((p) => p.type === "free") || null;
    setPlans(basePlans);
    setAddons(addonPlans);
    setFreePlan(freePlan);
    setCurrentRegion(region);
    const creditPlan = allPlans.find((p) => p.type === "credit_base") || null;
    setCreditPlan(creditPlan);
  };

  useEffect(() => {
    organizationService.subscribe("ORGANIZATION_AVAILABLE", initializeApp);

    if (isUserLoggedIn) {
      initializeApp();
    }

    return () => {
      organizationService.unsubscribe("ORGANIZATION_AVAILABLE", initializeApp);
    };
  }, [isUserLoggedIn]);

  useEffect(() => {
    if (isUserLoggedIn && (plans.length === 0 || addons.length === 0)) {
      initializeApp();
    }
  }, [isUserLoggedIn, plans, addons]);

  return (
    <AppContext.Provider value={{ addons, plans, currentRegion, freePlan, creditPlan }}>
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used inside AppProvider");
  }
  return context;
};
